//Node Modules
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLoaderData } from "react-router";

//GraphQL
import { API, graphqlOperation } from "aws-amplify";
import { getAlarmGroupForEdit, listDevicesAlarmGroup } from "../../../graphql/queries-custom";

//BinaryForge Components
import { PageHeader } from "../../../components/layout";
import { AlarmGroupForm } from "../../../components/alarm/groups";
import { Note } from "../../../components/helpers";

//3rd Party Components

//Atoms

//Helpers
import { getErrorMessage } from "../../../helpers/Error";

//Other

const AlarmGroupEdit = () => {
	//Hooks
	const { t } = useTranslation();
	const { alarmGroupId } = useLoaderData();

	//Local State
	const [groupInfo, setGroupInfo] = useState(null);
	const [errorMessage, setErrorMessage] = useState("");

	//On Page Load
	useEffect(() => {
		handleGetGroup();
	}, []);

	//Get the initial group data
	const handleGetGroup = async () => {
		try {
			setErrorMessage("");
			const {
				data: { getAlarmGroup: groupData },
			} = await API.graphql(graphqlOperation(getAlarmGroupForEdit, { id: alarmGroupId }));

			if (!groupData) {
				throw new Error("Alarm group not found");
			}

			if (!groupData.schemeId) {
				throw new Error("Alarm group is missing scheme information");
			}

			const {
				data: {
					deviceByScheme: { items: devicesData },
				},
			} = await API.graphql(
				graphqlOperation(listDevicesAlarmGroup, {
					schemeId: groupData.schemeId,
				}),
			);

			await processGroupData(groupData, devicesData || []);
		} catch (err) {
			console.error("Get Alarm Group Error ::", err);
			setGroupInfo(null);
			setErrorMessage(getErrorMessage(err));
		}
	};

	const processGroupData = async (groupData, devicesData) => {
		const devicesById = new Map(
			(devicesData ?? []).filter((device) => device?.id).map((device) => [device.id, device]),
		);
		const toSelectableDevice = (deviceId) => {
			const device = devicesById.get(deviceId);
			if (!device) return null;

			return {
				...device,
				alert: device.alert ?? { sensor_state: 0 },
			};
		};

		const inputItems = Array.isArray(groupData?.input?.items) ? groupData.input.items : [];
		const outputItems = Array.isArray(groupData?.output?.items) ? groupData.output.items : [];
		const groupId = groupData?.id;
		const groupName = groupData?.name;
		const inputDevices = inputItems
			.map((item) => ({ item, device: toSelectableDevice(item?.deviceID) }))
			.filter(({ device }) => Boolean(device?.id))
			.map((input) => ({
				...input.device,
				relId: input.item.id,
			}));
		const outputDevices = outputItems
			.map((item) => ({ item, device: toSelectableDevice(item?.deviceID) }))
			.filter(({ device }) => Boolean(device?.id))
			.map((output) => ({
				...output.device,
				relId: output.item.id,
			}));
		const inAlarm = inputDevices
			.filter((device) => Number(device?.alert?.sensor_state) === 1)
			.map((device) => ({ device }));

		setGroupInfo({ id: groupId, name: groupName, inputDevices, outputDevices, inAlarm });
	};

	return (
		<>
			<PageHeader
				title={t("alarm.group.edit.pageTitle", { groupId: groupInfo?.name ? groupInfo.name : alarmGroupId })}
				subtitle={t("alarm.group.edit.pageSubtitle")}
				enableBack
			/>
			<main className="appMain">
				<div className="appMain-content">
					{errorMessage ? (
						<Note messageKey={`Error: ${errorMessage}`} icon="pi-exclamation-circle fontColour-error" />
					) : (
						groupInfo && <AlarmGroupForm type="edit" initialData={groupInfo} groupId={alarmGroupId} />
					)}
				</div>
			</main>
		</>
	);
};

export default AlarmGroupEdit;
